import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Robot,
  ChatCircle,
  Lightning,
  Users,
  ChartLineUp,
  Check,
  WhatsappLogo,
  ArrowRight,
  Star,
  List,
  X
} from '@phosphor-icons/react';

const plans = [
  {
    id: 'free',
    name: 'Gratis',
    price: '$0',
    period: '/mes',
    description: 'Prueba 14 días + $50 de crédito',
    features: ['1 agente IA', '100 mensajes/mes', '1 canal', '$50 MXN de regalo'],
    cta: 'Comenzar gratis',
    highlighted: false
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '$199',
    period: '/mes',
    description: 'Para pequeños negocios',
    features: ['1 agente IA', '1,000 mensajes/mes', '1 canal WhatsApp', 'Soporte por email'],
    cta: 'Comenzar',
    highlighted: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$499',
    period: '/mes',
    description: 'Para negocios en crecimiento',
    features: ['3 agentes IA', '5,000 mensajes/mes', '3 canales', 'Base de conocimiento', 'Analíticas', 'Soporte prioritario'],
    cta: 'Comenzar',
    highlighted: true
  },
  {
    id: 'business',
    name: 'Business',
    price: '$999',
    period: '/mes',
    description: 'Para empresas',
    features: ['Agentes ilimitados', '20,000 mensajes/mes', 'Canales ilimitados', 'API acceso', 'Equipo ilimitado', 'Soporte dedicado'],
    cta: 'Contactar ventas',
    highlighted: false
  }
];

const features = [
  {
    icon: Robot,
    title: 'Agente IA Inteligente',
    description: 'Crea tu agente con un wizard conversacional. Sin código, sin complicaciones.'
  },
  {
    icon: WhatsappLogo,
    title: 'WhatsApp Integrado',
    description: 'Conecta tu número de WhatsApp Business en minutos con guías paso a paso.'
  },
  {
    icon: ChatCircle,
    title: 'Inbox Unificado',
    description: 'Monitorea todas las conversaciones. Toma el control cuando lo necesites.'
  },
  {
    icon: Lightning,
    title: 'Respuestas Instantáneas',
    description: 'Tu agente responde 24/7 con el tono y contexto de tu negocio.'
  },
  {
    icon: Users,
    title: 'Equipo Colaborativo',
    description: 'Asigna chats a tu equipo. Escala a humanos cuando sea necesario.'
  },
  {
    icon: ChartLineUp,
    title: 'Analíticas Detalladas',
    description: 'Métricas de rendimiento, tiempos de respuesta y satisfacción.'
  }
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Novagente",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "description": "Plataforma SaaS para crear agentes de inteligencia artificial conectados a WhatsApp Business. Automatiza la atencion al cliente sin programar.",
  "url": "https://novagente.io",
  "offers": [
    { "@type": "Offer", "name": "Gratis", "price": "0", "priceCurrency": "MXN" },
    { "@type": "Offer", "name": "Starter", "price": "199", "priceCurrency": "MXN" },
    { "@type": "Offer", "name": "Pro", "price": "499", "priceCurrency": "MXN" },
    { "@type": "Offer", "name": "Business", "price": "999", "priceCurrency": "MXN" }
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "50"
  }
};

export default function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E4E4E7]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/images/novagente-logo.png" alt="Novagente" className="h-8" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-[#52525B] hover:text-[#09090B] transition-colors">Características</a>
            <a href="#pricing" className="text-sm text-[#52525B] hover:text-[#09090B] transition-colors">Precios</a>
            <Link to="/login" className="text-sm text-[#52525B] hover:text-[#09090B] transition-colors">Iniciar sesión</Link>
            <Link
              to="/register"
              data-testid="nav-register-btn"
              className="bg-[#0047FF] text-white px-5 py-2.5 text-sm font-semibold rounded-full shadow-lg shadow-[#0047FF]/25 hover:bg-[#0035CC] hover:shadow-[#0047FF]/40 transition-all"
            >
              Comenzar gratis
            </Link>
          </div>
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={mobileMenuOpen}
            data-testid="mobile-menu-btn"
            className="md:hidden w-11 h-11 -mr-2 flex items-center justify-center text-[#09090B]"
          >
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-b border-[#E4E4E7] shadow-lg px-6 py-4"
          >
            <div className="flex flex-col">
              <a
                href="#features"
                onClick={() => setMobileMenuOpen(false)}
                className="py-3.5 text-base text-[#09090B] border-b border-[#F4F4F5]"
              >
                Características
              </a>
              <a
                href="#pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="py-3.5 text-base text-[#09090B] border-b border-[#F4F4F5]"
              >
                Precios
              </a>
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-login-btn"
                className="py-3.5 text-base text-[#09090B]"
              >
                Iniciar sesión
              </Link>
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-register-btn"
                className="mt-2 bg-[#0047FF] text-white text-center py-3.5 font-semibold rounded-full shadow-lg shadow-[#0047FF]/25"
              >
                Comenzar gratis
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
        {/* Soft background glows */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#0047FF]/10 rounded-full blur-3xl" />
          <div className="absolute top-64 -left-40 w-[400px] h-[400px] bg-indigo-400/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#0047FF]/5 border border-[#0047FF]/20 text-[#0047FF] px-4 py-1.5 rounded-full mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0047FF] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0047FF]" />
                </span>
                <span className="font-mono text-xs tracking-[0.15em] uppercase">Plataforma de Agentes IA</span>
              </div>
              <h1 className="font-black text-5xl md:text-6xl xl:text-7xl tracking-tighter leading-[0.95] text-[#09090B] mb-6">
                Tu negocio<br />
                atendido<br />
                <span className="bg-gradient-to-r from-[#0047FF] to-indigo-500 bg-clip-text text-transparent">24/7</span>
              </h1>
              <p className="text-lg text-[#52525B] mb-6 max-w-md leading-relaxed">
                Crea agentes IA que atienden a tus clientes por WhatsApp.
                Sin código. Configuración en minutos.
              </p>
              <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-full mb-8 text-sm font-medium">
                <Lightning size={16} weight="fill" />
                ¡Regístrate hoy y recibe $50 MXN de crédito gratis!
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  data-testid="hero-cta-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#0047FF] text-white px-8 py-4 font-semibold rounded-full shadow-xl shadow-[#0047FF]/30 hover:bg-[#0035CC] hover:shadow-[#0047FF]/40 hover:-translate-y-0.5 transition-all"
                >
                  Crear mi agente gratis
                  <ArrowRight size={20} />
                </Link>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 border border-[#E4E4E7] bg-white/60 px-8 py-4 font-semibold rounded-full text-[#09090B] hover:border-[#0047FF] hover:text-[#0047FF] transition-colors"
                >
                  Ver cómo funciona
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-[#52525B]">
                <span className="inline-flex items-center gap-1.5"><Check size={16} weight="bold" className="text-green-600" /> 14 días de prueba</span>
                <span className="inline-flex items-center gap-1.5"><Check size={16} weight="bold" className="text-green-600" /> Sin tarjeta de crédito</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              {/* Hero photo */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#0047FF]/10 aspect-[4/5] max-h-[560px] w-full">
                <img
                  src="/images/hero-photo.jpg"
                  alt="Persona escribiendo por WhatsApp con un agente IA de Novagente"
                  width="1200"
                  height="800"
                  className="w-full h-full object-cover"
                />
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#09090B]/30 via-transparent to-transparent" />
              </div>

              {/* Floating chat: customer message */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute top-8 -left-4 lg:-left-10 max-w-[240px] bg-white rounded-2xl rounded-tl-sm shadow-xl border border-[#E4E4E7] p-4"
              >
                <p className="text-sm text-[#09090B]">Hola, ¿tienen envíos a Guadalajara?</p>
                <p className="text-[10px] text-[#A1A1AA] mt-1 text-right">10:02 p.m.</p>
              </motion.div>

              {/* Floating chat: AI reply */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 1.1 }}
                className="absolute bottom-20 -right-4 lg:-right-8 max-w-[260px] bg-[#0047FF] rounded-2xl rounded-br-sm shadow-xl shadow-[#0047FF]/30 p-4"
              >
                <p className="text-sm text-white">¡Claro! Enviamos a todo México. ¿Te comparto los precios? 🚀</p>
                <p className="text-[10px] text-white/60 mt-1 flex items-center justify-end gap-1">
                  <Robot size={12} /> Respondido por IA · 10:02 p.m.
                </p>
              </motion.div>

              {/* Floating status chip */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.5 }}
                className="absolute -bottom-4 left-8 bg-white rounded-full shadow-lg border border-[#E4E4E7] px-4 py-2.5 flex items-center gap-2"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                </span>
                <span className="text-sm font-semibold text-[#09090B]">Agente activo 24/7</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 border-y border-[#E4E4E7] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#52525B] text-center mb-8">
            EMPRESAS QUE CONFÍAN EN NOVAGENTE
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-20">
            <a href="https://planetaenvios.com/" target="_blank" rel="noopener noreferrer"><img src="/images/cliente-1.png" alt="Planeta Envios" className="h-14 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" /></a>
            <a href="https://auraid.io/" target="_blank" rel="noopener noreferrer"><img src="/images/cliente-2.png" alt="AuraID" className="h-14 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" /></a>
            <a href="https://novacfdi.com/" target="_blank" rel="noopener noreferrer"><img src="/images/cliente-3.png" alt="NovaCFDI" className="h-14 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" /></a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#0047FF] mb-4">
              CARACTERÍSTICAS
            </p>
            <h2 className="font-black text-4xl md:text-5xl tracking-tighter text-[#09090B]">
              Todo lo que necesitas
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="p-8 bg-white rounded-2xl border border-[#E4E4E7] shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-[#0047FF]/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0047FF]/10 flex items-center justify-center mb-5">
                  <feature.icon size={26} weight="duotone" className="text-[#0047FF]" />
                </div>
                <h3 className="font-bold text-lg text-[#09090B] mb-2">{feature.title}</h3>
                <p className="text-[#52525B] leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 px-6 lg:px-8 bg-[#09090B] relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#0047FF]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#0047FF] mb-4">
              CÓMO FUNCIONA
            </p>
            <h2 className="font-black text-4xl md:text-5xl tracking-tighter text-white">
              En 3 simples pasos
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '01', title: 'Crea tu agente', desc: 'Responde preguntas simples y generamos tu agente IA personalizado.' },
              { step: '02', title: 'Conecta WhatsApp', desc: 'Sigue nuestra guía paso a paso para vincular tu número.' },
              { step: '03', title: 'Empieza a atender', desc: 'Tu agente responde automáticamente. Toma el control cuando quieras.' }
            ].map((item) => (
              <div key={item.step} className="p-8 rounded-2xl bg-[#18181B] border border-[#27272A] hover:border-[#0047FF]/50 transition-colors">
                <span className="font-mono text-6xl font-black bg-gradient-to-br from-[#0047FF] to-indigo-400 bg-clip-text text-transparent">{item.step}</span>
                <h3 className="font-bold text-xl text-white mt-4 mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 lg:px-8 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#0047FF] mb-4">
              PRECIOS EN PESOS MEXICANOS
            </p>
            <h2 className="font-black text-4xl md:text-5xl tracking-tighter text-[#09090B]">
              Planes simples y transparentes
            </h2>
            <p className="text-[#52525B] mt-4">Todos los precios están en MXN</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {plans.map((plan) => (
              <div
                key={plan.id}
                data-testid={`plan-${plan.id}`}
                className={`relative p-8 rounded-2xl flex flex-col transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[#0047FF] to-indigo-600 text-white shadow-2xl shadow-[#0047FF]/30 lg:-translate-y-3'
                    : 'bg-white border border-[#E4E4E7] shadow-sm hover:shadow-lg hover:-translate-y-1'
                }`}
              >
                {plan.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-white text-[#0047FF] text-xs font-bold px-3 py-1 rounded-full shadow-md">
                    <Star size={12} weight="fill" /> POPULAR
                  </span>
                )}
                <h3 className={`font-bold text-xl mb-1 ${plan.highlighted ? 'text-white' : 'text-[#09090B]'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm mb-4 ${plan.highlighted ? 'text-white/70' : 'text-[#52525B]'}`}>
                  {plan.description}
                </p>
                <div className="mb-6">
                  <span className={`font-black text-4xl ${plan.highlighted ? 'text-white' : 'text-[#09090B]'}`}>
                    {plan.price}
                  </span>
                  <span className={plan.highlighted ? 'text-white/70' : 'text-[#52525B]'}>
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check size={16} weight="bold" className={plan.highlighted ? 'text-white' : 'text-[#0047FF]'} />
                      <span className={plan.highlighted ? 'text-white' : 'text-[#52525B]'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid={`plan-${plan.id}-cta`}
                  className={`block w-full text-center py-3 font-semibold rounded-full transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#0047FF] hover:bg-[#F4F4F5]'
                      : 'bg-[#09090B] text-white hover:bg-[#27272A]'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#0047FF] to-indigo-600 px-8 py-20 text-center relative overflow-hidden shadow-2xl shadow-[#0047FF]/30">
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-indigo-300/20 rounded-full blur-3xl" />
          </div>
          <div className="relative">
            <h2 className="font-black text-4xl md:text-5xl tracking-tighter text-white mb-6">
              Empieza hoy.<br />Es gratis.
            </h2>
            <p className="text-lg text-white/80 mb-8">
              14 días de prueba sin compromiso. Sin tarjeta de crédito.
            </p>
            <Link
              to="/register"
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-2 bg-white text-[#0047FF] px-8 py-4 font-bold rounded-full shadow-xl hover:bg-[#F4F4F5] hover:-translate-y-0.5 transition-all"
            >
              Crear mi agente ahora
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-8 border-t border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            <img src="/images/novagente-logo.png" alt="Novagente" className="h-6" />
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terminos" className="text-[#52525B] hover:text-[#09090B] transition-colors">
              Terminos y Condiciones
            </Link>
            <Link to="/privacidad" className="text-[#52525B] hover:text-[#09090B] transition-colors">
              Aviso de Privacidad
            </Link>
          </div>
          <p className="text-sm text-[#52525B]">
            © {new Date().getFullYear()} Novagente. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
